/* Kanban board view — kanban-parity REBUILD (2026-07-12).
 *
 * The floor is top-tier (Trello / Monday / Linear): populated lanes FLEX to
 * fill the width and their cards WRAP into a responsive multi-column grid, so
 * one populated lane spreads across the board instead of a 15-card strip in a
 * 300px channel; empty lanes collapse into ONE quiet group that reveals its
 * drop targets on click or during a drag. Manual within-lane ordering (drag a
 * card to reposition) and quick-add at a lane foot are added on top.
 *
 * The evidence-gated machine is UNTOUCHED and stays sacred:
 *  - a CROSS-lane drag IS a status transition through the ONE shared guarded
 *    path (applyTransition in board.ts); legality is double-locked (the pure
 *    resolver laneDropCandidates client-side + the server's PATCH_TRANSITIONS);
 *  - Verified / Regressed hold NO drop target (dropTargets:[]) — a drag can
 *    never reach them; the lanes are drop-DISABLED (dimmed with --muted, NEVER
 *    red — ineligible ≠ adverse);
 *  - Attested is dialog-only (dropTargets:[]) — its evidence note can't ride a
 *    drop; it is set from the card status menu;
 *  - a drop onto Won't-do opens the SAME required-reason ask(); a cancel snaps
 *    back with NO PATCH;
 *  - manual ordering is PURE presentation state (ws.boardOrder → POST
 *    /me/board-order): it NEVER touches a task row, its status, or the stall
 *    clock, and the derived overdue→priority→category sort stays the DEFAULT
 *    until the AM's first manual drag in a lane;
 *  - quick-add creates a `custom:` task through the SAME guarded create/PATCH
 *    path — only on lanes with a legal starting status, never an evidence lane.
 *
 * The card's compact status button (⋮) opens the SAME statusMenu — the
 * keyboard/touch move path. Full per-task edits stay in the List row expander.
 */
import { html, nothing, render, type TemplateResult } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { cap } from '../lib/format.ts';
import { applyTransition, exportButton, filterBar, laneSort, selectionBar, statusMenu, viewToggle } from './board.ts';
import { labelChip } from './labels.ts';
import { checklistBadge } from './checklist.ts';
import { openTaskPanel } from './taskDetail.ts';
import { renderActions } from '../views/actions.ts';
import { payloadStore } from '../state/payload.ts';
import type { Workspace } from '../state/workspace.ts';
import type { WsTask } from '../types/workspace.ts';

// Lane occupancy role (Design ruling c617fde, "occupancy-aware kanban lanes"):
//  - 'working' lanes (Open/Implemented/Waiting) NEVER collapse — they always
//    render full-width with honest empty copy inside when empty, and stay drop
//    targets (an empty working lane is signal, not noise);
//  - the 'exception' lane (Regressed) leads when occupied, else folds to the
//    collapsed empty-group;
//  - 'receipt' lanes (Verified/Attested/Won't-do) show a collapsed "Show N"
//    receipt when occupied, else fold to the group (receipts are only
//    interesting when they have receipts).
type LaneRole = 'working' | 'exception' | 'receipt';

interface KLane {
  key: string;
  label: string;
  role: LaneRole;
  statuses: string[];    // task statuses that render in this lane
  dropTargets: string[]; // statuses a drop MAY set; [] = evidence-only, never a drop target
  accent: string;        // left-edge accent class shared with .wsrow (regressed/implemented/waiting)
  closed?: boolean;      // Verified / Attested / Won't-do — the capped receipt tail
  addStatus?: string;    // quick-add creates a custom task at this status (absent = no quick-add)
  emptyCopy?: string;    // honest empty-state line shown inside an empty WORKING lane
}

// Exception-first order (Regressed leads when occupied, the receipt tail is
// capped). Columns/order are FIXED in code — no add/rename affordance (audit §7
// non-goal). The `addStatus` lanes are the only ones with a legal STARTING
// status for a hand-created task — evidence lanes (Regressed/Verified/Attested)
// and Won't-do carry none, so quick-add can never fabricate an evidence state.
export const LANES: KLane[] = [
  { key: 'regressed', label: 'Regressed', role: 'exception', statuses: ['regressed'], dropTargets: [], accent: 'regressed' },
  { key: 'implemented', label: 'Implemented', role: 'working', statuses: ['implemented'], dropTargets: ['implemented'], accent: 'implemented', addStatus: 'implemented', emptyCopy: 'Nothing awaiting verification.' },
  { key: 'waiting', label: 'Waiting', role: 'working', statuses: ['waiting-client', 'waiting-platform'], dropTargets: ['waiting-client', 'waiting-platform'], accent: 'waiting', addStatus: 'waiting-client', emptyCopy: 'Nothing waiting on a client or platform.' },
  { key: 'active', label: 'Open / in progress', role: 'working', statuses: ['open', 'in-progress'], dropTargets: ['open'], accent: '', addStatus: 'open', emptyCopy: 'No open items yet.' },
  { key: 'verified', label: 'Verified', role: 'receipt', statuses: ['verified'], dropTargets: [], accent: '', closed: true },
  // Q-03: a DISTINCT closed lane, separate from Verified. dropTargets:[] — a drag
  // can never reach it (the mandatory note can't ride a drop); it is set only via
  // the statusMenu → "Mark human-attested" dialog. Never folded into Verified.
  { key: 'attested', label: 'Human-attested', role: 'receipt', statuses: ['attested'], dropTargets: [], accent: '', closed: true },
  { key: 'wontdo', label: "Won't-do", role: 'receipt', statuses: ['wont-do'], dropTargets: ['wont-do'], accent: '', closed: true },
];

/* Pure resolver (unit-tested): given a card's current status and a target lane,
   which legal target statuses does the lane offer? Reads ws.legalDropTargets
   (the server's PATCH_TRANSITIONS row) — so verified/regressed, absent from
   every transition value, can NEVER be returned as a target. A same-lane drop
   yields [] (the card's own status is never in its lane's dropTargets legally). */
export function laneDropCandidates(ws: Workspace, from: string, lane: KLane): string[] {
  const legal = ws.legalDropTargets(from);
  return lane.dropTargets.filter((s) => legal.includes(s));
}

/* The cards for a lane, in DISPLAY order: the derived sort (Waiting by stall
   age, everything else the ONE shared laneSort) with any manual per-view order
   applied on top (ws.orderLaneCards — derived stays the default until a drag).
   Shared by the renderer and the reorder-drop handler so they never disagree. */
function laneCardsFor(ws: Workspace, rows: WsTask[], lane: KLane): WsTask[] {
  const cards = rows.filter((t) => lane.statuses.includes(t.status));
  const derived = lane.key === 'waiting'
    ? (a: WsTask, b: WsTask) => ws.stateAge(b) - ws.stateAge(a)
    : laneSort(ws);
  return ws.orderLaneCards(lane.key, cards, derived);
}

// module-level drag/UI state (cleared on dragend/drop or re-render):
let dragTid: number | null = null;         // id of the card being dragged
let reorderTargetId: number | null = null; // the card a within-lane drop lands against
let reorderAfter = false;                   // insert AFTER the target (else before)
let emptyGroupOpen = false;                 // the empty-lanes group is click-expanded
let quickAddLaneKey: string | null = null;  // which lane's foot is in quick-add mode

function clearInsertion(board: Element | null | undefined): void {
  board?.querySelectorAll('.kins-before,.kins-after,.kins-before-x,.kins-after-x')
    .forEach((c) => c.classList.remove('kins-before', 'kins-after', 'kins-before-x', 'kins-after-x'));
}

function clearDragChrome(board: Element | null | undefined): void {
  board?.classList.remove('kdragging');
  clearInsertion(board);
  board?.querySelectorAll<HTMLElement>('[data-lane]').forEach((laneEl) => {
    laneEl.classList.remove('kdrop-ok', 'kdrop-disabled');
    laneEl.removeAttribute('aria-disabled');
  });
}

function onDragStart(ws: Workspace, ev: DragEvent, t: WsTask): void {
  dragTid = t.id;
  reorderTargetId = null;
  if (ev.dataTransfer) {
    ev.dataTransfer.setData('text/plain', String(t.id));
    ev.dataTransfer.effectAllowed = 'move';
  }
  const card = ev.currentTarget as HTMLElement;
  card.classList.add('kdragging');
  const board = card.closest('[data-kboard]');
  // reveal the empty-lanes group so its lanes become live drop targets mid-drag
  board?.classList.add('kdragging');
  // mark every lane ok/disabled: a legal transition target OR this card's OWN
  // lane (a within-lane reorder). Evidence lanes stay disabled (--muted, never
  // red). Includes the now-revealed empty lanes.
  board?.querySelectorAll<HTMLElement>('[data-lane]').forEach((laneEl) => {
    const laneObj = LANES.find((l) => l.key === laneEl.dataset.laneKey);
    const isOrigin = !!laneObj && laneObj.statuses.includes(t.status);
    const targets = (laneEl.dataset.targets || '').split(',').filter(Boolean);
    const ok = isOrigin || laneDropCandidates(ws, t.status, { dropTargets: targets } as KLane).length > 0;
    laneEl.classList.toggle('kdrop-ok', ok);
    laneEl.classList.toggle('kdrop-disabled', !ok);
    if (!ok) laneEl.setAttribute('aria-disabled', 'true');
    else laneEl.removeAttribute('aria-disabled');
  });
}

function onDragEnd(ev: DragEvent): void {
  dragTid = null;
  reorderTargetId = null;
  (ev.currentTarget as HTMLElement).classList.remove('kdragging');
  clearDragChrome((ev.currentTarget as HTMLElement).closest('[data-kboard]'));
}

/* Within-lane reorder: while hovering a sibling card in the DRAGGED card's own
   lane, show an insertion indicator and record where the drop lands. Auto-
   detects single vs multi-column (a card sharing this row ⇒ compare X, else Y)
   so the indicator reads right in both the narrow and the wide (grid) layout.
   stopPropagation suppresses the lane-level handler so a card hover wins. */
function onCardDragOver(ws: Workspace, ev: DragEvent, t: WsTask, lane: KLane): void {
  if (dragTid == null || dragTid === t.id) return;
  const dragged = ws.tasks.find((x) => x.id === dragTid);
  if (!dragged || !lane.statuses.includes(dragged.status)) return; // only reorder within origin lane
  ev.preventDefault();
  ev.stopPropagation();
  const card = ev.currentTarget as HTMLElement;
  const r = card.getBoundingClientRect();
  const sibs = [...(card.parentElement?.querySelectorAll<HTMLElement>('.kcard') || [])];
  const multi = sibs.some((s) => s !== card && Math.abs(s.getBoundingClientRect().top - r.top) < 6);
  const before = multi ? ev.clientX < r.left + r.width / 2 : ev.clientY < r.top + r.height / 2;
  reorderTargetId = t.id;
  reorderAfter = !before;
  const board = card.closest('[data-kboard]');
  clearInsertion(board);
  card.classList.add(multi ? (before ? 'kins-before-x' : 'kins-after-x') : (before ? 'kins-before' : 'kins-after'));
}

function onLaneDragOver(ws: Workspace, ev: DragEvent, lane: KLane): void {
  if (dragTid == null) return;
  const t = ws.tasks.find((x) => x.id === dragTid);
  if (!t) return;
  // origin lane: allow a drop in the lane's empty space (append at end) and
  // clear any card-level insertion indicator (the drop is a reorder, not a move)
  if (lane.statuses.includes(t.status)) {
    ev.preventDefault();
    reorderTargetId = null;
    clearInsertion((ev.currentTarget as HTMLElement).closest('[data-kboard]'));
    return;
  }
  // otherwise allow a drop ONLY when this lane is a legal transition target — the
  // browser then fires `drop` only on legal lanes (belt-and-suspenders below).
  if (laneDropCandidates(ws, t.status, lane).length > 0) ev.preventDefault();
}

async function onLaneDrop(ws: Workspace, ev: DragEvent, lane: KLane): Promise<void> {
  ev.preventDefault();
  const id = dragTid;
  const tgtId = reorderTargetId;
  const after = reorderAfter;
  dragTid = null;
  reorderTargetId = null;
  clearDragChrome((ev.currentTarget as HTMLElement).closest('[data-kboard]'));
  const t = ws.tasks.find((x) => x.id === id);
  if (!t) return;
  const from = t.status;

  // WITHIN-lane drop → manual REORDER (presentation state only, no PATCH, the
  // stall clock never moves). Permute the lane's current display order.
  if (lane.statuses.includes(from)) {
    if (id == null) return;
    const current = laneCardsFor(ws, ws.filterProg(ws.tasks), lane).map((x) => x.id);
    const without = current.filter((x) => x !== id);
    let idx = without.length; // default: append at the lane foot
    if (tgtId != null) {
      const p = without.indexOf(tgtId);
      if (p >= 0) idx = after ? p + 1 : p;
    }
    without.splice(idx, 0, id);
    if (without.join(',') !== current.join(',')) await ws.saveLaneOrder(lane.key, without);
    return;
  }

  // CROSS-lane drop → status transition through the ONE shared guarded path.
  const candidates = laneDropCandidates(ws, from, lane);
  if (!candidates.length) {
    ws.toast(`"${t.title.slice(0, 40)}" can't move to ${lane.label} from ${ws.STATUS_FULL[from] || from}.`, '');
    return;
  }
  let to = candidates[0];
  if (!to) return; // narrows for TS (candidates.length checked above)
  // Waiting sub-picker: if both waiting-client and waiting-platform are legal,
  // ask which; a cancel snaps back with no PATCH.
  if (candidates.length > 1) {
    const pick = await ws.ask({
      title: 'Waiting on…',
      options: candidates.map((s) => ws.STATUS_FULL[s] || s),
      ok: 'Set',
    });
    if (!pick) return;
    to = candidates.find((s) => (ws.STATUS_FULL[s] || s) === pick) || candidates[0] || to;
  }
  // Won't-do requires a reason (parity with the list statusMenu); a cancel/empty
  // reason snaps back with NO PATCH — a cancelled drag is never a forced move.
  if (to === 'wont-do') {
    const reason = await ws.ask({
      title: "Why won't this be done?",
      multiline: true,
      hint: 'Required — kept on the task record so the next run marks it known instead of re-flagging it.',
      ok: "Won't do",
    });
    if (!reason) return;
    await applyTransition(ws, t, to, reason);
    return;
  }
  await applyTransition(ws, t, to);
}

// the label-chip cap on a card (Design gate K5): at most this many chips, then
// a "·N" overflow — a card is a scan unit; the full set lives in the drawer.
const MAX_CARD_LABELS = 3;

function kcard(ws: Workspace, t: WsTask, lane: KLane): TemplateResult {
  const age = ws.stateAge(t);
  const overdue = ws.overdueDays(t);
  const accent = t.status === 'regressed' ? 'regressed'
    : t.status === 'implemented' ? 'implemented'
    : /^waiting/.test(t.status) ? 'waiting' : '';
  const labels = t.labels || [];
  return html`
    <div class="kcard ${accent}${ws._selected.has(t.id) ? ' selected' : ''}" data-tid="${t.id}" draggable="true"
      role="listitem" aria-roledescription="draggable task card"
      @dragstart=${(ev: DragEvent) => onDragStart(ws, ev, t)}
      @dragover=${(ev: DragEvent) => onCardDragOver(ws, ev, t, lane)}
      @dragend=${(ev: DragEvent) => onDragEnd(ev)}>
      <div class="ktop">
        <input type="checkbox" class="wssel" aria-label="select task for bulk action"
          .checked=${ws._selected.has(t.id)}
          @click=${(ev: MouseEvent) => ev.stopPropagation()}
          @change=${(ev: Event) => ws.toggleSelect(t.id, (ev.target as HTMLInputElement).checked)}>
        <b class="ktitle td-open" role="button" tabindex="0" title="${t.title} — open task detail"
          @click=${(ev: MouseEvent) => { ev.stopPropagation(); const fresh = ws.tasks.find((x) => x.id === t.id) || t; openTaskPanel(ws, fresh); }}
          @keydown=${(ev: KeyboardEvent) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); const fresh = ws.tasks.find((x) => x.id === t.id) || t; openTaskPanel(ws, fresh); } }}>${t.title}</b>
      </div>
      <div class="kmeta">
        ${/* the effort chip is DROPPED from cards (priority + effort in identical
             dress read as "Medium … Medium"); effort lives in the list row. */ nothing}
        <span class="prio ${t.priority}">${cap(t.priority)}</span>
        <span class="pill-meta">${t.category || ''}</span>
        ${/* overdue SUPPRESSES the age chip — "Nd late" already implies age, and
             a card carries at most one age/overdue chip. */
          overdue > 0
            ? html`<span class="wsage late">⚠ ${overdue}d late</span>`
            : (age > 0 && /^waiting|^open$|in-progress|implemented/.test(t.status)
                ? html`<span class="wsage ${age > 14 ? 'hot' : ''}">${age}d</span>` : nothing)}
        ${labels.length
          ? html`<span class="wslabels">${labels.slice(0, MAX_CARD_LABELS).map((l) => labelChip(l))}${labels.length > MAX_CARD_LABELS ? html`<span class="pill-meta" title="${labels.length - MAX_CARD_LABELS} more label${labels.length - MAX_CARD_LABELS === 1 ? '' : 's'} — see the list view">·${labels.length - MAX_CARD_LABELS}</span>` : nothing}</span>`
          : nothing}
        ${checklistBadge(t)}
        ${/* Touch (SIGNED deferral): this status button IS the sanctioned touch
             move path (tap → the legal-moves menu). ICON-ONLY (⋮) — the lane
             already IS the status; the status NAME rides the aria-label/title. */ nothing}
        <button class="wsst s-${t.status} kmove" data-act="status" aria-haspopup="true"
          aria-label="${ws.STATUS_FULL[t.status] || t.status} — change status"
          title="${ws.STATUS_FULL[t.status] || t.status} — move (keyboard- and touch-accessible)"
          @click=${(ev: MouseEvent) => { ev.stopPropagation(); const fresh = ws.tasks.find((x) => x.id === t.id) || t; statusMenu(ws, ev.currentTarget as HTMLElement, fresh); }}>${ws.ICON.kebab}</button>
      </div>
    </div>`;
}

/* Quick-add at a lane foot (Trello's add-a-card). Only on lanes with a legal
   starting status (active/waiting/implemented) — never an evidence lane. The
   button flips to an inline input; Enter creates a `custom:` task and keeps the
   input open for the next (enter-to-chain); Escape closes. */
function startQuickAdd(laneKey: string): void {
  quickAddLaneKey = laneKey;
  renderActions(payloadStore.get());
}

async function onQuickAddKey(ws: Workspace, ev: KeyboardEvent, lane: KLane): Promise<void> {
  if (ev.key === 'Escape') {
    ev.preventDefault();
    quickAddLaneKey = null;
    renderActions(payloadStore.get());
    return;
  }
  if (ev.key !== 'Enter') return;
  ev.preventDefault();
  const inp = ev.currentTarget as HTMLInputElement;
  const title = inp.value.trim();
  if (!title || !lane.addStatus) return;
  inp.value = ''; // clear at once so a fast typist can chain
  // quickAddLaneKey stays set → the post-create refresh repaints the still-open
  // input (and the tail of renderKanban re-focuses it).
  await ws.quickAddTask(title, lane.addStatus);
}

function quickAddFoot(ws: Workspace, lane: KLane): TemplateResult | typeof nothing {
  if (!lane.addStatus) return nothing;
  if (quickAddLaneKey === lane.key) {
    return html`
      <div class="kaddform">
        <input type="text" aria-label="new card title in ${lane.label}"
          placeholder="Card title — Enter to add, Esc to close"
          @keydown=${(ev: KeyboardEvent) => onQuickAddKey(ws, ev, lane)}>
        <span class="kaddhint">Adds a custom task to ${lane.label} · Enter to add another</span>
      </div>`;
  }
  return html`<button class="kadd" @click=${() => startQuickAdd(lane.key)}>${ws.ICON.plus}Add card</button>`;
}

function laneSection(ws: Workspace, lane: KLane, cards: WsTask[]): TemplateResult {
  const evidenceOnly = lane.dropTargets.length === 0;
  // keyed repeat (task id): a within-lane reorder / status move MOVES card DOM
  // nodes instead of rewriting their contents (per-node state survives).
  const cardList = cards.length
    ? repeat(cards, (t) => t.id, (t) => kcard(ws, t, lane))
    : nothing;
  // the receipt tail (Verified / Attested / Won't-do) collapse behind a count so
  // they don't render a full column of cards; an EMPTY working lane shows honest
  // copy instead (Design ruling: an empty working lane is signal AND a drop
  // target, never noise).
  const body = lane.closed && cards.length
    ? html`<details class="klane-collapse"><summary>Show ${cards.length} ${cards.length === 1 ? 'card' : 'cards'}</summary>${cardList}</details>`
    : !cards.length && lane.role === 'working'
      ? html`<div class="klane-empty-copy">${lane.emptyCopy}</div>`
      : cardList;
  // a populated non-receipt lane GROWS to fill the width (kfilled); an empty
  // working lane holds the fixed 300px so it never becomes a dead ocean.
  const filled = cards.length > 0 && !lane.closed;
  return html`
    <section class="klane${lane.closed ? ' closed' : ''}${filled ? ' kfilled' : ''}" data-lane data-lane-key="${lane.key}" data-targets="${lane.dropTargets.join(',')}"
      role="group" aria-label="${lane.label}, ${cards.length} ${cards.length === 1 ? 'task' : 'tasks'}"
      @dragover=${(ev: DragEvent) => onLaneDragOver(ws, ev, lane)}
      @drop=${(ev: DragEvent) => onLaneDrop(ws, ev, lane)}>
      <div class="klane-head">
        <span class="klane-title">${lane.label}</span>
        <span class="cnt">${cards.length}</span>
        ${evidenceOnly ? html`<span class="klane-lock" title="${lane.key === 'attested' ? 'human-attested — set from the card’s status menu with a required evidence note, never by drag' : 'evidence-only — reached by a recheck probe or the next run, never by drag'}">${ws.ICON.check}</span>` : nothing}
      </div>
      <div class="klane-body" role="list">${body}${quickAddFoot(ws, lane)}</div>
    </section>`;
}

/* The ONE quiet collapsed group for every empty lane (never a row of rotated
   rails). Collapsed = a slim vertical bar; a click OR an active drag reveals
   the empty lanes as stacked drop targets (CSS reveals them; a drag highlights
   the LEGAL ones via the same .kdrop-ok vocabulary the populated lanes use). */
function emptyGroup(ws: Workspace, emptyLanes: KLane[]): TemplateResult {
  const n = emptyLanes.length;
  return html`
    <div class="kempty-group${emptyGroupOpen ? ' open' : ''}" data-empty-group>
      <button class="kempty-toggle" aria-expanded=${emptyGroupOpen ? 'true' : 'false'}
        aria-label="${n} empty lane${n === 1 ? '' : 's'} — click to ${emptyGroupOpen ? 'hide' : 'show'}"
        @click=${() => { emptyGroupOpen = !emptyGroupOpen; renderActions(payloadStore.get()); }}>
        <span class="kempty-toggle-label">${n} empty lane${n === 1 ? '' : 's'} ${emptyGroupOpen ? '◂' : '▸'}</span>
      </button>
      <div class="kempty-lanes">
        ${emptyLanes.map((lane) => laneSection(ws, lane, []))}
      </div>
    </div>`;
}

function kanbanToolbar(ws: Workspace): TemplateResult {
  return html`
    <div style="display:flex;gap:8px;margin-bottom:14px;flex-wrap:wrap;align-items:center">
      ${viewToggle(ws)}
      <button class="wsmini" @click=${() => ws.addCustomTask()}>${ws.ICON.plus}Add task</button>
      ${exportButton(ws)}
      <label style="font-size:11.5px;color:var(--muted);display:inline-flex;align-items:center;gap:5px;margin-left:auto">
        <input type="checkbox" ?checked=${ws._mineOnly} @change=${(ev: Event) => { ws._mineOnly = (ev.target as HTMLInputElement).checked; renderActions(payloadStore.get()); }}> my tasks</label>
      <label style="font-size:11.5px;color:var(--muted);display:inline-flex;align-items:center;gap:5px">
        <input type="checkbox" ?checked=${ws._progOnly} @change=${(ev: Event) => { ws._progOnly = (ev.target as HTMLInputElement).checked; renderActions(payloadStore.get()); }}> agreed program only</label>
    </div>`;
}

/* The honest zero-state (Design ruling tier 1): a board with NO tasks at all
   never shows a grid of empty lanes — it shows one quiet panel that names the
   reason and offers the run. Gated on the UNFILTERED task count so a filter that
   hides everything still shows the lanes (a filtered-empty board is a different,
   honest state — the working lanes' own empty copy covers it). */
function emptyBoard(): TemplateResult {
  return html`
    <div class="kboard-empty" data-kboard-empty>
      <div class="kboard-empty-title">No work items yet</div>
      <div class="kboard-empty-body">Run research and the board derives them — every opportunity, conflict and threat becomes a task with stable identity across re-runs.</div>
      <div class="kboard-empty-body kboard-empty-note">Research isn't dispatched from this screen yet — a refresh action arrives with the run-dispatch increment. (The old "Run report" chrome button was retired with the CRM shell; wiring a dead button here would be worse than saying so.)</div>
    </div>`;
}

export function renderKanban(ws: Workspace, el: HTMLElement): void {
  const rows = ws.filterProg(ws.tasks);
  const laneData = LANES.map((lane) => ({ lane, cards: laneCardsFor(ws, rows, lane) }));
  // occupancy-aware partition (Design ruling c617fde): working lanes ALWAYS
  // render in the board row (empty ones with honest copy); an exception/receipt
  // lane renders in the row only when occupied, else folds to the empty-group.
  const mainLanes = laneData.filter((d) => d.lane.role === 'working' || d.cards.length > 0);
  const groupLanes = laneData.filter((d) => d.lane.role !== 'working' && d.cards.length === 0);
  const boardEmpty = ws.tasks.length === 0; // tier 1: no tasks at all → no grid
  render(html`
    <div class="finding">Board view — drag a card to a legal lane to change its status (the same guarded move as the list menu), or drag within a lane to reorder it your way. <b>Verified &amp; Regressed are evidence-only</b> (recheck / next run), never a drop target.</div>
    ${kanbanToolbar(ws)}
    ${filterBar(ws)}
    ${selectionBar(ws)}
    ${boardEmpty
      ? emptyBoard()
      : html`<div class="kboard" data-kboard>
      ${mainLanes.map((d) => laneSection(ws, d.lane, d.cards))}
      ${groupLanes.length ? emptyGroup(ws, groupLanes.map((d) => d.lane)) : nothing}
    </div>
    <div class="caption">The three working lanes always show (empty ones name what's missing); exception &amp; receipt lanes fold into the group on the right until they have cards, and reveal as drop targets when you drag. Populated lanes fill the width. Manual card order is your view only — it never changes a task or its stall clock. Full task edits — assign, due, program, notes, recheck — are in the <b>List</b> view.</div>`}`, el);

  // keep the quick-add input focused across the create→refresh→repaint cycle
  // (enter-to-chain) — lit doesn't autofocus, so re-assert it here.
  if (quickAddLaneKey) {
    const inp = el.querySelector<HTMLInputElement>(`.klane[data-lane-key="${quickAddLaneKey}"] .kaddform input`);
    if (inp && document.activeElement !== inp) inp.focus();
  }
}
